import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { X, ChevronDown, Search } from "lucide-react";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import "./LocationFilterModal.css";

import { getFlagEmoji } from '@/utils/countryFlags';

interface LocationFilterModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedLocations: string[];
  onLocationToggle: (locationId: string) => void;
  onResetFilters: () => void;
  locations: Array<{ id: string; label: string }>;
}

const LocationFilterModal: React.FC<LocationFilterModalProps> = ({
  isOpen,
  onOpenChange,
  selectedLocations,
  onLocationToggle,
  onResetFilters,
  locations,
}) => {
  // Sample data for regions and languages (you would replace with actual data)
  const regions = [
    { id: "north-africa", label: "North Africa" },
    { id: "west-africa", label: "West Africa" },
    { id: "east-africa", label: "East Africa" },
    { id: "southern-africa", label: "Southern Africa" },
    { id: "central-africa", label: "Central Africa" },
  ];

  const regionCountries: Record<string, { id: string; label: string }[]> = {
    "north-africa": locations.filter((c) =>
      ["Egypt", "Algeria", "Morocco", "Tunisia", "Libya", "Sudan"].includes(
        c.label
      )
    ),
    "west-africa": locations.filter((c) =>
      [
        "Nigeria",
        "Ghana",
        "Senegal",
        "Ivory Coast",
        "Mali",
        "Burkina Faso",
        "Niger",
        "Guinea",
        "Benin",
        "Togo",
        "Sierra Leone",
        "Liberia",
        "Gambia",
        "Cape Verde",
      ].includes(c.label)
    ),
    "east-africa": locations.filter((c) =>
      [
        "Kenya",
        "Ethiopia",
        "Tanzania",
        "Uganda",
        "Rwanda",
        "Burundi",
        "Somalia",
        "South Sudan",
        "Djibouti",
        "Eritrea",
      ].includes(c.label)
    ),
    "southern-africa": locations.filter((c) =>
      [
        "South Africa",
        "Botswana",
        "Namibia",
        "Lesotho",
        "Eswatini",
        "Zimbabwe",
        "Zambia",
        "Malawi",
        "Mozambique",
        "Angola",
      ].includes(c.label)
    ),
    "central-africa": locations.filter((c) =>
      [
        "Cameroon",
        "Chad",
        "Congo",
        "DR Congo",
        "Gabon",
        "Equatorial Guinea",
        "Central African Republic",
        "Sao Tome and Principe",
      ].includes(c.label)
    ),
  };

  const languages = [
    { id: "english", label: "English" },
    { id: "french", label: "French" },
    { id: "arabic", label: "Arabic" },
    { id: "swahili", label: "Swahili" },
    { id: "portuguese", label: "Portuguese" },
  ];

  // Language groups and mapping (mocked for demo, adjust as needed)
  const languageCountries: Record<string, { id: string; label: string }[]> = {
    english: locations.filter((c) =>
      [
        "Nigeria",
        "Ghana",
        "Kenya",
        "Uganda",
        "South Africa",
        "Botswana",
        "Namibia",
        "Zimbabwe",
        "Zambia",
        "Malawi",
        "Sierra Leone",
        "Liberia",
        "Gambia",
      ].includes(c.label)
    ),
    french: locations.filter((c) =>
      [
        "Senegal",
        "Ivory Coast",
        "Mali",
        "Burkina Faso",
        "Niger",
        "Guinea",
        "Benin",
        "Togo",
        "Cameroon",
        "Chad",
        "Congo",
        "DR Congo",
        "Gabon",
        "Central African Republic",
      ].includes(c.label)
    ),
    arabic: locations.filter((c) =>
      [
        "Egypt",
        "Algeria",
        "Morocco",
        "Tunisia",
        "Libya",
        "Sudan",
        "Djibouti",
        "Somalia",
      ].includes(c.label)
    ),
    swahili: locations.filter((c) =>
      [
        "Kenya",
        "Tanzania",
        "Uganda",
        "Rwanda",
        "Burundi",
        "DR Congo",
        "Mozambique",
      ].includes(c.label)
    ),
    portuguese: locations.filter((c) =>
      [
        "Mozambique",
        "Angola",
        "Guinea-Bissau",
        "Cape Verde",
        "Sao Tome and Principe",
      ].includes(c.label)
    ),
  };

  const [activeTab, setActiveTab] = useState<
    "countries" | "regions" | "languages"
  >("countries");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const tabRefs = {
    countries: useRef<HTMLButtonElement>(null),
    regions: useRef<HTMLButtonElement>(null),
    languages: useRef<HTMLButtonElement>(null),
  };
  const indicatorRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });
  const [expandedRegion, setExpandedRegion] = useState<string | null>(
    "north-africa"
  );
  const [expandedLanguage, setExpandedLanguage] = useState<string | null>(
    "english"
  );

  // Add effect to reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("countries");
      setIsSearchMode(false);
      setSearchValue("");
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    // Use a timeout to ensure DOM is updated
    const timeout = setTimeout(() => {
      const node = tabRefs[activeTab].current;
      if (node) {
        const { offsetLeft, offsetWidth } = node;
        const extra = 16; // 8px padding on each side
        setIndicatorStyle({
          left: offsetLeft - extra / 2,
          width: offsetWidth + extra,
          opacity: 1,
        });
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [activeTab, isSearchMode, isOpen]);

  // Filtered countries for search
  const filteredCountries = locations.filter((country) =>
    country.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  // When applying filters, close the modal and call the parent's apply function
  const handleApplyFilters = () => {
    onOpenChange(false);
  };

  // When resetting filters, also clear search and exit search mode
  const handleResetFilters = () => {
    setIsSearchMode(false);
    setSearchValue("");
    onResetFilters();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="h-full flex flex-col justify-end items-center max-w-none bg-white/20 backdrop-blur-md border-none shadow-none">
        {/* Custom centered close button at the top */}
        <div className="">
          <DialogClose className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors">
            <X size={16} className="text-gray-500" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>
        {/* Center card container with max width */}
        <div className="relative w-full max-w-[700px] bg-white rounded-xl overflow-hidden flex flex-col h-[88vh] md:h-[90vh] ">
          {/* Tab group and search icon */}
          <div className="flex justify-center w-full pt-6 pb-2">
            {!isSearchMode ? (
              <div className="flex justify-center items-center gap-1 md:gap-2 w-full max-w-[520px] mx-auto md:px-2">
                <div className="relative inline-flex items-center px-1 py-1 border border-zinc-200 rounded-full bg-white">
                  {[
                    { key: "countries", label: "Countries" },
                    { key: "regions", label: "Regions" },
                    { key: "languages", label: "Languages" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      ref={
                        tabRefs[
                          tab.key as "countries" | "regions" | "languages"
                        ]
                      }
                      className={`px-4 py-2 text-[14px] font-medium rounded-full transition-all focus:outline-none
                        ${
                          activeTab === tab.key
                            ? "bg-[#f2f2f5] text-[#202020] font-medium"
                            : "bg-transparent text-gray-500 font-medium hover:bg-zinc-50"
                        }
                      `}
                      onClick={() =>
                        setActiveTab(
                          tab.key as "countries" | "regions" | "languages"
                        )
                      }
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <button
                  className="md:ml-2 flex items-center justify-center w-10 h-10 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors"
                  onClick={() => setIsSearchMode(true)}
                  aria-label="Search countries"
                >
                  <Search size={20} className="text-zinc-400" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 w-full max-w-[420px] mx-auto px-4">
                <input
                  type="text"
                  autoFocus
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search countries..."
                  className="flex-1 px-4 py-2 rounded-full border border-zinc-200 bg-zinc-50 text-base focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <button
                  className="ml-2 flex items-center justify-center w-10 h-10 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors"
                  onClick={() => {
                    setIsSearchMode(false);
                    setSearchValue("");
                  }}
                  aria-label="Close search"
                >
                  <X size={20} className="text-zinc-400" />
                </button>
              </div>
            )}
          </div>

          {/* Modal content */}
          <ScrollArea className="flex-1 pr-4 px-4 md:px-6">
            <div className="space-y-3 py-2">
              {/* Tab content or search content */}
              {!isSearchMode ? (
                <div className="transition-all duration-250 ease-in-out">
                  {activeTab === "countries" && (
                    <div className="space-y-3 py-2">
                      {locations.map((country) => (
                        <div
                          key={country.id}
                          className="flex items-start space-x-3"
                        >
                          <Checkbox
                            id={`country-${country.id}`}
                            checked={selectedLocations.includes(country.label)}
                            onCheckedChange={() =>
                              onLocationToggle(country.label)
                            }
                            className="mt-0.5 rounded-sm border-gray-300 text-accent"
                          />
                          <span className="inline-flex items-center gap-2" title={country.label}>
                            {getFlagEmoji(country.label) || getFlagEmoji(country.id)}
                          </span>
                          <label
                            htmlFor={`country-${country.id}`}
                            className="text-sm text-gray-700 cursor-pointer"
                          >
                            {country.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                  {activeTab === "regions" && (
                    <div className="pr-4 px-6">
                      <Accordion
                        type="single"
                        collapsible
                        value={expandedRegion || undefined}
                        onValueChange={setExpandedRegion}
                        className="w-full"
                      >
                        {regions.map((region) => (
                          <AccordionItem value={region.id} key={region.id}>
                            <AccordionTrigger className="py-2 text-gray-800 font-medium hover:no-underline">
                              {region.label}
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="max-h-[300px] overflow-y-auto">
                                <div className="space-y-3 py-2">
                                  {regionCountries[region.id]?.length ? (
                                    regionCountries[region.id].map(
                                      (country) => (
                                        <div
                                          key={country.id}
                                          className="flex items-start space-x-3"
                                        >
                                          <Checkbox
                                            id={`region-country-${country.id}`}
                                            checked={selectedLocations.includes(
                                              country.label
                                            )}
                                            onCheckedChange={() =>
                                              onLocationToggle(country.label)
                                            }
                                            className="mt-0.5 rounded-sm border-gray-300 text-accent"
                                          />
                                          <span className="inline-flex items-center gap-2" title={country.label}>
                                            {getFlagEmoji(country.label) || getFlagEmoji(country.id)}
                                          </span>
                                          <label
                                            htmlFor={`region-country-${country.id}`}
                                            className="text-sm text-gray-700 cursor-pointer"
                                          >
                                            {country.label}
                                          </label>
                                        </div>
                                      )
                                    )
                                  ) : (
                                    <div className="text-gray-400 text-sm">
                                      No countries in this region.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  )}
                  {activeTab === "languages" && (
                    <div className="pr-4 px-6">
                      <Accordion
                        type="single"
                        collapsible
                        value={expandedLanguage || undefined}
                        onValueChange={setExpandedLanguage}
                        className="w-full"
                      >
                        {languages.map((language) => (
                          <AccordionItem value={language.id} key={language.id}>
                            <AccordionTrigger className="py-2 text-gray-800 font-medium hover:no-underline">
                              {language.label}
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="max-h-[300px] overflow-y-auto">
                                <div className="space-y-3 py-2">
                                  {languageCountries[language.id]?.length ? (
                                    languageCountries[language.id].map(
                                      (country) => (
                                        <div
                                          key={country.id}
                                          className="flex items-start space-x-3"
                                        >
                                          <Checkbox
                                            id={`language-country-${country.id}`}
                                            checked={selectedLocations.includes(
                                              country.label
                                            )}
                                            onCheckedChange={() =>
                                              onLocationToggle(country.label)
                                            }
                                            className="mt-0.5 rounded-sm border-gray-300 text-accent"
                                          />
                                          <span className="inline-flex items-center gap-2" title={country.label}>
                                            {getFlagEmoji(country.label) || getFlagEmoji(country.id)}
                                          </span>
                                          <label
                                            htmlFor={`language-country-${country.id}`}
                                            className="text-sm text-gray-700 cursor-pointer"
                                          >
                                            {country.label}
                                          </label>
                                        </div>
                                      )
                                    )
                                  ) : (
                                    <div className="text-gray-400 text-sm">
                                      No countries for this language.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3 py-2">
                  {filteredCountries.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      No countries found.
                    </div>
                  ) : (
                    filteredCountries.map((country) => (
                      <div
                        key={country.id}
                        className="flex items-start space-x-3"
                      >
                        <Checkbox
                          id={`country-search-${country.id}`}
                          checked={selectedLocations.includes(country.label)}
                          onCheckedChange={() =>
                            onLocationToggle(country.label)
                          }
                          className="mt-0.5 rounded-sm border-gray-300 text-accent"
                        />
                        <span className="inline-flex items-center gap-2" title={country.label}>
                          {getFlagEmoji(country.label) || getFlagEmoji(country.id)}
                        </span>
                        <label
                          htmlFor={`country-search-${country.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          {country.label}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer with action buttons */}
          <div className="flex flex-row justify-end gap-3 p-6 bg-white border-t border-gray-100">
            <Button
              variant="outline"
              onClick={handleResetFilters}
              className="text-sm px-8 py-3 w-1/2 md:w-auto"
            >
              Reset Filters
            </Button>
            <Button
              onClick={handleApplyFilters}
              className="text-sm px-8 py-3 w-1/2 md:w-auto relative"
              disabled={selectedLocations.length === 0}
            >
              Apply Filters
              {selectedLocations.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-accent text-white text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
                  {selectedLocations.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationFilterModal;
